/**
 * Copyright 2016 xdot GmbH
 *
 * Author: Marius Runde
 * Date: 05.07.2016
 *
 */

'use strict';

import { amdRequire } from 'plugin/liferay-widgets-react-web';
import DrawerGeoJsonLayer from '../../layer/DrawerGeoJsonLayer';
import translateDraw from './translate-leaflet-drawer';
import L from 'external/leaflet';
import { TextLabelControl } from './TextLabelControl';
import { ColorSelectControl } from './ColorSelectControl';
import { ColorPickerControl } from './ColorPickerControl';
import { textLabelIcon } from './TextLabelIcon';
import { asFeature, getFeature } from '../../utils/feature-util';
import { svgMarkerIcon } from './SvgMarkers';
import { boIcon } from '../../utils/icons';

L.LayerGroup.include({
    toGeoJSON: function(precision) {
        const type = this.feature && this.feature.geometry && this.feature.geometry.type;
        if (type === 'MultiPoint') {
            return this.toMultiPoint(precision);
        }

        const isGeometryCollection = type === 'GeometryCollection',
            jsons = [];

        this.eachLayer(function(layer) {
            if (layer.toGeoJSON) {
                const json = layer.toGeoJSON(precision);
                if (layer.options.radius) {
                    json.properties.radius = layer._mRadius;
                }
                if (isGeometryCollection) {
                    jsons.push(json.geometry);
                } else {
                    const feature = asFeature(json);
                    // Squash nested feature collections
                    if (feature.type === 'FeatureCollection') {
                        jsons.push.apply(jsons, feature.features);
                    } else {
                        jsons.push(feature);
                    }
                }
            }
        });

        if (isGeometryCollection) {
            return getFeature(this, {
                geometries: jsons,
                type: 'GeometryCollection'
            });
        }

        return {
            type: 'FeatureCollection',
            features: jsons
        };
    }
});

export default class DrawerPlugin {

    constructor(props) {
        console.log('Drawer constructor()');
        this.openTextLabelModal = props.openTextLabelModal && props.openTextLabelModal.bind(this);
        this.onDrawerChange = props.onDrawerChange && props.onDrawerChange.bind(this);

        this.handleTextLabelClick = this.handleTextLabelClick.bind(this);
        this.handleIconLabelClick = this.handleIconLabelClick.bind(this);
        this.handleLayerClick = this.handleLayerClick.bind(this);
        this.toggleColorPickerControl = this.toggleColorPickerControl.bind(this);
        this.onToggleTextLabelControl = this.onToggleTextLabelControl.bind(this);
        this.disableTextLabelControl = this.disableTextLabelControl.bind(this);
        this.disableColorPickerControl = this.disableColorPickerControl.bind(this);
        this.block = this.block.bind(this);
        this.unblock = this.unblock.bind(this);

        this.boundRequestHandled = false;
        this.boundRequestCb = this.boundRequestCb.bind(this);

        this.drawerColor = '#0000FF';

        this.o = {};
    }

    init(map, o) {
        this.o = o;
        amdRequire([ 'FlexiColorPicker', 'leaflet-draw', 'css!leaflet-draw-css' ], () => {
            const {
                drawerGeoJSON,
                drawOptions = {},
                drawerPluginTextLabel = true,
                drawerPluginColorPicker = true
            } = this.o;
            translateDraw();
            this.map = map;
            map.options.drawControl = true;

            const options = {
                onEachFeature: (feature, layer) => {
                    let onClick = this.handleLayerClick;
                    if (layer.drawerType === 'TextLabel') {
                        onClick = this.handleTextLabelClick;
                    } else if (layer.drawerType === 'IconLabel') {
                        onClick = this.handleIconLabelClick;
                    }
                    layer.on('click', onClick);
                }
            };

            this.drawerGeoJsonLayer = new DrawerGeoJsonLayer(drawerGeoJSON, options).addTo(map);

            this.drawControl = new L.Control.Draw({
                draw: {
                    //circle: false,
                    circlemarker: false,
                    ...drawOptions,
                },
                edit: {
                    featureGroup: this.drawerGeoJsonLayer,
                    edit: {
                        selectedPathOptions: {
                            maintainColor: true
                        }
                    }
                }
            });

            map.addControl(this.drawControl);

            if (drawerPluginTextLabel) {
                this.textLabelControl = new TextLabelControl({ onClick: this.onToggleTextLabelControl });
                map.addControl(this.textLabelControl);
            }

            if (drawerPluginColorPicker) {
                this.colorPickerControl = new ColorPickerControl();
                this.colorSelectionControl = new ColorSelectControl({
                    onClick: this.toggleColorPickerControl,
                    color: this.drawerColor
                });
                map.addControl(this.colorSelectionControl);
            }
            this.updateDrawingOptions();

            //map.on('click', this.handleClick.bind(this));
            map.on('mousedown', this.handleClick.bind(this));
            map.on('touchstart', this.handleClick.bind(this));

            map.on(L.Draw.Event.CREATED, this.handleCreated.bind(this));
            map.on(L.Draw.Event.EDITED, this.handleEdited.bind(this));
            map.on(L.Draw.Event.DELETED, this.handleDeleted.bind(this));

            map.on(L.Draw.Event.DRAWSTART, this.handleDrawStart.bind(this));
            map.on(L.Draw.Event.DRAWSTOP, this.handleDrawStop.bind(this));
            map.on(L.Draw.Event.EDITSTART, this.handleEditStart.bind(this));
            map.on(L.Draw.Event.EDITSTOP, this.handleEditStop.bind(this));
            map.on(L.Draw.Event.DELETESTART, this.handleDeleteStart.bind(this));
            map.on(L.Draw.Event.DELETESTOP, this.handleDeleteStop.bind(this));
        });
    }

    initOptions(o) {
        const { drawerGeoJSON } = o;
        //todo use a  simple fast bound calc algo
        const drawerGeoJsonLayer = new DrawerGeoJsonLayer(drawerGeoJSON);
        const bounds = drawerGeoJsonLayer.getBounds();
        if (bounds.isValid()) {
            o.boundRequest = {
                bounds,
                options: { animate: false },
                cb: this.boundRequestCb
            };
        }
        return o;
    }

    update(map, o) {
        const { drawerGeoJSON, drawerTextLabel } = o || {};

        if (drawerGeoJSON && this.drawerGeoJsonLayer && !this.editControlActive && !this.deleteControlActive) {
            this.drawerGeoJsonLayer.clearLayers();
            this.drawerGeoJsonLayer.addData(drawerGeoJSON);
        }

        if (drawerTextLabel && drawerTextLabel.edited) {
            this.handleTextLabelEdited(drawerTextLabel);
        }
    }

    updateDrawingOptions() {
        if (this.colorSelectionControl) {
            this.colorSelectionControl.setColor(this.drawerColor);
        }

        this.drawControl.setDrawingOptions({
            polygon: {
                shapeOptions: {
                    color: this.drawerColor
                }
            },
            polyline: {
                shapeOptions: {
                    color: this.drawerColor
                }
            },
            rectangle: {
                shapeOptions: {
                    color: this.drawerColor
                }
            },
            circle: {
                shapeOptions: {
                    color: this.drawerColor
                }
            },
            // use ClassName marker based on ClassName(type) or default
            marker: this.o.type
                ? { icon: boIcon(this.o.type) }
                : {
                    icon: L.divIcon({
                        html: svgMarkerIcon(this.drawerColor),
                        className: 'svg-icon',
                        iconSize: [ 32, 52 ],
                        iconAnchor: [ 16, 52 ],
                    })
                },
        });
    }

    boundRequestCb() {
        this.boundRequestHandled = true;
    }

    onToggleTextLabelControl(active) {
        if (active) {
            this.colorPickerControl.disable();
        }
    }

    disableTextLabelControl() {
        this.textLabelControl && this.textLabelControl.isActive() && this.textLabelControl.disable();
    }

    disableColorPickerControl() {
        this.colorPickerControl && this.colorPickerControl.isActive() && this.colorPickerControl.disable();
    }

    toggleColorPickerControl(e) {
        if (this.colorPickerControl) {
            this.disableTextLabelControl();
            if (!this.colorPickerControl.isActive()) {
                this.colorPickerControl.enable();
                this.map.addControl(this.colorPickerControl);
                let slider = this.colorPickerControl.getSliderElement();
                let picker = this.colorPickerControl.getPickerElement();
                // eslint-disable-next-line no-undef
                ColorPicker(slider, picker, (hex) => {
                    this.drawerColor = hex;
                    this.updateDrawingOptions();
                });
            } else {
                this.disableColorPickerControl();
                this.map.removeControl(this.colorPickerControl);
            }
        }
    }

    handleClick(e) {
        if (this.textLabelControl && this.textLabelControl.isActive()) {
            this.textLabelControl.disable();
            this.openTextLabelModal(e.latlng, null);
        }
    }

    handleLayerClick(e) {
        if (this.colorPickerControl && this.colorPickerControl.isActive()) {
            L.DomEvent.stop(e);
            let clickedLayer = e.target;
            clickedLayer.options.color = this.drawerColor;
            this.transformToGeoJSON();
        }
    }

    handleTextLabelClick(e) {
        if (this.colorPickerControl && this.colorPickerControl.isActive()) {
            this.handleLayerClick(e);
        } else if (!this.deleteControlActive) {
            this.disableTextLabelControl();
            this.openTextLabelModal(e.latlng, e.target);
        }
    }

    handleTextLabelEdited(drawerTextLabel) {
        const { oldLayer, text } = drawerTextLabel;
        let color = this.drawerColor;

        if (oldLayer) {
            this.drawerGeoJsonLayer.getLayers().forEach((layer) => {
                if (layer.options.id === oldLayer.options.id) {
                    this.drawerGeoJsonLayer.removeLayer(layer);
                }
            });

            color = oldLayer.options.color;
        }

        let textLabelMarker = L.marker(drawerTextLabel.coordinates, {
            icon: textLabelIcon({
                ...drawerTextLabel,
                color
            }),
            color,
            title: text,
            editFormData: {
                ...drawerTextLabel,
                color: color
            }
        });

        L.Util.stamp(textLabelMarker);

        this.handleCreated({ layer: textLabelMarker });
    }

    handleIconLabelClick(e) {
        if (this.colorPickerControl && this.colorPickerControl.isActive()) {
            let marker = e.target;
            let newIcon = L.divIcon({
                html: svgMarkerIcon(this.drawerColor),
                className: 'svg-icon',
                iconSize: [ 32, 52 ],
                iconAnchor: [ 16, 52 ],
            });
            marker.setIcon(newIcon);
            this.transformToGeoJSON();
        } else if (!this.deleteControlActive) {
            this.disableColorPickerControl();
            this.disableTextLabelControl();
        }
    }

    handleCreated(e) {
        let layer = e.layer;
        layer.options.color = layer.options.color || this.drawerColor;
        L.Util.stamp(layer);
        this.drawerGeoJsonLayer.addLayer(layer);
        this.transformToGeoJSON();
    }

    handleEdited(e) {
        this.transformToGeoJSON();
    }

    handleDeleted(e) {
        e.layers.getLayers().forEach((layer) => {
            this.drawerGeoJsonLayer.removeLayer(layer);
        });

        this.transformToGeoJSON();
    }

    block() {
        this.map.blockingControlActive = true;
    }

    unblock() {
        /*
        hacky but Leaflet Draw ensures closing click for rectangle creation is
        propagated to map which triggers our LayerInfo plugin.
        https://github.com/Leaflet/Leaflet.draw/blob/develop/src/draw/handler/Draw.Rectangle.js#L52
        This ensures LayerInfo stays closed while drawing
         */

        setTimeout(() => {this.map.blockingControlActive = false;}, 300);
    }

    handleDrawStart() {
        this.drawControlActive = true;
        this.block();
        this.disableTextLabelControl();
        // this.disableColorPickerControl();
    }

    handleDrawStop() {
        this.drawControlActive = false;
        this.unblock();
    }

    handleEditStart() {
        this.editControlActive = true;
        this.block();
        this.disableTextLabelControl();
        this.disableColorPickerControl();
    }

    handleEditStop() {
        this.editControlActive = false;
        this.unblock();
    }

    handleDeleteStart() {
        this.deleteControlActive = true;
        this.block();
        this.disableTextLabelControl();
        this.disableColorPickerControl();
    }

    handleDeleteStop() {
        this.deleteControlActive = false;
        this.unblock();
    }

    transformToGeoJSON() {
        let geoJSON = this.drawerGeoJsonLayer.toGeoJSON();
        for (let i = 0 ; i < geoJSON.features.length ; i++) {
            let feature = geoJSON.features[i];
            let layer = this.drawerGeoJsonLayer.getLayers()[i];

            feature.style = {
                ...feature.style,
                color: layer.options.color,
            };

            //Ensure that Icon has properties before setting it, otherwise it disappears from the map after placing the marker
            let hasIconProperties = Boolean(layer.options.icon && Object.keys(layer.options.icon.options).length);
            feature.properties = {
                ...feature.properties,
                icon: hasIconProperties ? layer.options.icon.options : null,
                id: L.Util.stamp(layer),
                title: layer.options.title,
                editFormData: layer.options.editFormData,
                // set Icon based on object type if initial icon is null
                type: this.o.type,
            };
        }

        this.onDrawerChange(geoJSON);
    }

}



// WEBPACK FOOTER //
// ./js/plugins/DrawerPlugin/DrawerPlugin.js